import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@restworld/utils-common';

/**
 * Text size scale for body copy, labels, and captions.
 *
 * Size scale:
 * - 1: xs (12px / 18px line-height)
 * - 2: sm (14px / 21px)
 * - 3: base (16px / 24px) — default
 * - 4: lg (20px / 30px)
 */
const textVariants = cva('whitespace-pre-wrap', {
  variants: {
    size: {
      1: 'text-xs leading-[18px]',
      2: 'text-sm leading-[21px]',
      3: 'text-base leading-6',
      4: 'text-[20px] leading-[30px]',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    color: {
      default: 'text-gray-600',
      foreground: 'text-gray-900',
      muted: 'text-gray-500',
      'muted-light': 'text-gray-400',
      primary: 'text-primary-800',
      'primary-light': 'text-primary-700',
      'primary-dark': 'text-primary-900',
      'primary-surface': 'text-primary-100',
      secondary: 'text-secondary-800',
      error: 'text-red-600',
      success: 'text-green-700',
      warning: 'text-orange-700',
      white: 'text-white',
      black: 'text-black',
      purple: 'text-purple-700',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
  },
  defaultVariants: {
    size: 3,
    weight: 'normal',
    color: 'default',
  },
});

type TextTag = 'p' | 'span' | 'div' | 'label';

export type TextProps = VariantProps<typeof textVariants> & {
  /** The HTML element to render. Default: 'p' */
  as?: TextTag;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Render raw HTML instead of children (mutually exclusive with children). */
  dangerouslySetInnerHTML?: { __html: string };
  /** Max characters before truncation with ellipsis */
  maxChars?: number;
};

export const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ as: Tag = 'p', size, weight, color, align, children, className, maxChars, ...props }, ref) => {
    let content = children;
    if (maxChars && typeof children === 'string' && children.length > maxChars) {
      content = children.slice(0, maxChars) + '…';
    }

    return React.createElement(
      Tag,
      {
        ref,
        className: cn(textVariants({ size, weight, color, align }), className),
        ...props,
      },
      content
    );
  }
);
Text.displayName = 'Text';

export { textVariants };
export default Text;
